import { useAsyncData } from 'nuxt/app';
import { ref, toValue, watch, type MaybeRefOrGetter } from 'vue';
import { useGetData } from '~/composables/useGetData';
import type { LaravelPaginatedResponse } from '~/types/plugins/api';

/**
 * District (Lower House) and regional (Upper House) candidate counts for a
 * suburb — reused from the same `/candidates` endpoint the Candidates page
 * uses, filtered down to `per_page=1` since only `meta.total` is needed.
 */
export function useVotingAreaCounts(suburbId: MaybeRefOrGetter<string | null>) {
  const lowerHouseCount = ref<number | null>(null);
  const upperHouseCount = ref<number | null>(null);
  const pending = ref(false);
  const error = ref<unknown>(null);

  const fetchCount = async (position: 'lower' | 'upper', id: string) => {
    const { execute } = await useGetData<LaravelPaginatedResponse<unknown>>();
    const result = await execute('candidates', {
      params: {
        'filter[suburb_id]': id,
        'filter[position]': position,
        per_page: 1,
      },
    });
    return result?.meta?.total ?? 0;
  };

  // Guards against a slower in-flight request for a previous suburb
  // resolving after a newer one (or a clear) has already landed, which would
  // otherwise overwrite the current state with stale counts.
  let requestId = 0;

  const load = async () => {
    const id = toValue(suburbId);
    const currentRequestId = ++requestId;

    if (!id) {
      lowerHouseCount.value = null;
      upperHouseCount.value = null;
      pending.value = false;
      return;
    }

    pending.value = true;
    error.value = null;

    try {
      const [lower, upper] = await Promise.all([
        fetchCount('lower', id),
        fetchCount('upper', id),
      ]);
      if (currentRequestId !== requestId) return;
      lowerHouseCount.value = lower;
      upperHouseCount.value = upper;
    } catch (err) {
      if (currentRequestId !== requestId) return;
      error.value = err;
    } finally {
      if (currentRequestId === requestId) {
        pending.value = false;
      }
    }
  };

  // The very first call to `load()` is wrapped in `useAsyncData` below so
  // Nuxt awaits it on the server — this `watch` used to run with
  // `{ immediate: true }` and drive that first call itself, a fire-and-forget
  // invocation Nuxt never waited on, rendering the VotingAreaCard with its
  // null defaults and refetching the same counts again after hydration.
  // Every later suburb change still goes through this plain (non-immediate)
  // watch, exactly as before.
  watch(() => toValue(suburbId), load);

  // `lowerHouseCount`/`upperHouseCount`/`pending`/`error` are plain refs
  // local to this composable instance, so they don't survive SSR->client
  // hydration on their own — only `useAsyncData`'s own `data` is transferred
  // in the payload. The fetcher below mutates them via `load()` (as it always
  // has) and also returns a snapshot of the result, which the `watch`
  // beneath seeds back into those same refs on the client, where `load()`
  // itself never actually runs (its result already sits in the payload
  // under this key).
  const initialSuburbId = toValue(suburbId);
  const { data: initialResult } = useAsyncData<{
    lowerHouseCount: number | null;
    upperHouseCount: number | null;
    // A POJO, never the raw error: `error.value` is a real Error instance,
    // and Nuxt's payload serializer (devalue) throws on arbitrary non-POJOs —
    // so returning it here would turn a transient API failure during SSR into
    // a full page render failure instead of a zeroed card.
    errorMessage: string | null;
  } | null>(
    `voting-area-counts-${initialSuburbId ?? 'none'}`,
    async () => {
      await load();
      return {
        lowerHouseCount: lowerHouseCount.value,
        upperHouseCount: upperHouseCount.value,
        errorMessage: error.value instanceof Error ? error.value.message : null,
      };
    },
    { default: () => null },
  );

  watch(
    initialResult,
    (result) => {
      if (!result) return;
      lowerHouseCount.value = result.lowerHouseCount;
      upperHouseCount.value = result.upperHouseCount;
      error.value = result.errorMessage ? new Error(result.errorMessage) : null;
      pending.value = false;
    },
    { immediate: true },
  );

  return { lowerHouseCount, upperHouseCount, pending, error };
}
